import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { LuCheck, LuChevronsUpDown } from "react-icons/lu";
import { toast } from "sonner";

import { fetchCurrencies } from "@/actions/currency";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { FormControl, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface ICurrenciesProps {
    isPending: boolean;
    value: string | undefined;
    onChange: (currency: string | undefined) => void;
}

export const Currencies = ({ value, onChange, isPending }: ICurrenciesProps) => {
    const t = useTranslations("expense");

    const [ isOpen, setIsOpen ] = useState(false);

    const handleSelect = (currencyId: string) => {
        onChange(currencyId);
        setIsOpen(false);
    };

    const {
        data: currencies,
        error,
        isLoading,
    } = useQuery({
        queryKey: [ "currencies" ],
        queryFn: fetchCurrencies,
    });

    if (error) {
        toast.error(t("error.fetchCurrencies"));
        return null;
    }

    if (isLoading) {
        return <Skeleton className="h-[40px] w-full" />;
    }

    return (
        <>
            {currencies ? (
                <>
                    <Popover open={ isOpen } onOpenChange={ setIsOpen }>
                        <PopoverTrigger asChild>
                            <FormControl>
                                <Button
                                    disabled={ isPending }
                                    variant="outline"
                                    role="combobox"
                                    className={ cn("w-full justify-between", !value && "text-muted-foreground") }
                                >
                                    {value ? currencies.find(currency => currency.id === value)?.symbol : t("selectCurrency")}
                                    <LuChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                                </Button>
                            </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="p-0">
                            <Command>
                                <CommandInput placeholder={ t("searchCurrency") } />
                                <CommandList>
                                    <CommandEmpty>{t("noCurrencyFound")}</CommandEmpty>
                                    <CommandGroup>
                                        {currencies.map(currency => (
                                            <CommandItem
                                                key={ currency.id }
                                                value={ `${ currency.symbol } (${ currency.name })` }
                                                onSelect={ () => handleSelect(currency.id) }
                                            >
                                                {currency.symbol} ({currency.name})
                                                <LuCheck className={ cn("ml-auto", currency.id === value ? "opacity-100" : "opacity-0") } />
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                    <FormMessage />
                </>
            ) : null}
        </>
    );
};
